import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";

export default function Portal() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);

  useEffect(() => { if (user) api.get("/bookings/me").then((r) => setBookings(r.data)); }, [user]);

  if (loading) return <div className="max-w-4xl mx-auto py-16 px-4">{t({ en: "Loading…", fr: "Chargement…" })}</div>;
  if (!user) return <Navigate to="/login?redirect=/portal" replace />;

  const cancel = async (id) => {
    if (!window.confirm(t({ en: "Cancel this booking?", fr: "Annuler cette réservation?" }))) return;
    try {
      const r = await api.post(`/bookings/${id}/cancel`);
      const late = r.data.late_cancel_charge_applies;
      toast[late ? "warning" : "success"](
        late ? t({ en: "Cancelled — late cancellation fee applies (under 24h).", fr: "Annulé — frais d'annulation tardive s'appliquent (moins de 24 h)." }) : t({ en: "Booking cancelled.", fr: "Réservation annulée." })
      );
      const b = await api.get("/bookings/me"); setBookings(b.data);
    } catch (e) { toast.error(e.response?.data?.detail || t({ en: "Failed to cancel", fr: "Échec de l'annulation" })); }
  };

  const upcoming = bookings.filter((b) => new Date(b.start_utc) > new Date() && !b.status.startsWith("cancelled"));
  const past = bookings.filter((b) => new Date(b.start_utc) <= new Date() || b.status.startsWith("cancelled"));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16" data-testid="portal-page">
      <div className="overline mb-3">{t({ en: "Client portal", fr: "Espace client" })}</div>
      <h1 className="font-serif text-4xl mb-2 tracking-tight" style={{ color: "var(--ink)" }}>{t({ en: "Welcome,", fr: "Bienvenue," })} {user.name.split(" ")[0]}.</h1>
      <p style={{ color: "var(--ink-2)" }} className="mb-10">{t({ en: "Manage your bookings and access client-only resources.", fr: "Gérez vos réservations et accédez aux ressources réservées aux client·es." })}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <QuickCard label={t({ en: "Book a session", fr: "Prendre rendez-vous" })} href="/book" cta={t({ en: "Schedule", fr: "Planifier" })} />
        <QuickCard label={t({ en: "Complete intake form", fr: "Remplir le formulaire d'admission" })} href="/intake" cta={t({ en: "Fill out form", fr: "Remplir" })} />
        <QuickCard label={t({ en: "Client Library", fr: "Bibliothèque client" })} href="/library" cta={t({ en: "Browse resources", fr: "Parcourir" })} />
      </div>

      <section className="mb-12">
        <h2 className="font-serif text-2xl mb-4" style={{ color: "var(--ink)" }}>{t({ en: "Upcoming", fr: "À venir" })}</h2>
        {upcoming.length === 0 ? (
          <p style={{ color: "var(--ink-3)" }}>{t({ en: "No upcoming sessions.", fr: "Aucune séance à venir." })} <Link to="/book" className="link-underline">{t({ en: "Book one →", fr: "En réserver une →" })}</Link></p>
        ) : (
          <div className="space-y-3">{upcoming.map((b) => <BookingRow key={b.id} b={b} onCancel={() => cancel(b.id)} t={t} lang={lang} />)}</div>
        )}
      </section>

      <section>
        <h2 className="font-serif text-2xl mb-4" style={{ color: "var(--ink)" }}>{t({ en: "Past & cancelled", fr: "Passé et annulé" })}</h2>
        {past.length === 0 ? <p style={{ color: "var(--ink-3)" }}>{t({ en: "No history yet.", fr: "Aucun historique." })}</p> :
          <div className="space-y-3">{past.map((b) => <BookingRow key={b.id} b={b} t={t} lang={lang} />)}</div>}
      </section>
    </div>
  );
}

function BookingRow({ b, onCancel, t, lang }) {
  const locale = lang === "fr" ? "fr-CA" : "en-CA";
  const dt = new Date(b.start_utc).toLocaleString(locale, { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="card-warm p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`booking-${b.id}`}>
      <div>
        <div className="font-serif text-lg" style={{ color: "var(--ink)" }}>{b.session_name}</div>
        <div className="text-sm" style={{ color: "var(--ink-2)" }}>{dt} · {b.modality === "virtual" ? t({ en: "Virtual", fr: "Virtuel" }) : t({ en: "In-person", fr: "En présentiel" })}</div>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={b.status} t={t} />
        {onCancel && !b.status.startsWith("cancelled") && (
          <button onClick={onCancel} className="text-sm underline" style={{ color: "var(--ink-3)" }} data-testid={`cancel-${b.id}`}>{t({ en: "Cancel", fr: "Annuler" })}</button>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    confirmed: { c: "var(--brand)", label: t({ en: "Confirmed", fr: "Confirmée" }) },
    pending_payment: { c: "var(--ochre)", label: t({ en: "Payment pending", fr: "Paiement en attente" }) },
    cancelled: { c: "var(--ink-3)", label: t({ en: "Cancelled", fr: "Annulée" }) },
    cancelled_late: { c: "var(--terracotta)", label: t({ en: "Late cancel", fr: "Annulation tardive" }) },
  };
  const s = map[status] || { c: "var(--ink-3)", label: status };
  return <span className="text-[11px] font-mono uppercase tracking-widest px-3 py-1 rounded-full" style={{ background: "var(--surface-muted)", color: s.c, border: `1px solid ${s.c}` }}>{s.label}</span>;
}

function QuickCard({ label, href, cta }) {
  return (
    <Link to={href} className="card-warm p-6 block hover:-translate-y-1 transition-transform">
      <div className="font-serif text-xl mb-3" style={{ color: "var(--ink)" }}>{label}</div>
      <span className="link-underline text-sm">{cta} →</span>
    </Link>
  );
}
